/**
 * App.tsx — mango-web
 * PURPOSE: Root application component. Sets up routing, auth initialization,
 * and toast notifications. Auth routes are public; all others require AuthGuard.
 */

import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Login } from '@/routes/auth/Login';
import { Signup } from '@/routes/auth/Signup';
import { Home } from '@/routes/Home';
import { AuthGuard } from '@/components/AuthGuard';
import '@/styles/auth.css';

// --- Component ---

/**
 * PURPOSE: Root app shell — initializes auth, renders routes, provides toasts.
 * - Connected to: stores/authStore.ts → initialize on mount
 * - Connected to: routes/auth/Login.tsx → public route
 * - Connected to: routes/auth/Signup.tsx → public route
 * - Connected to: routes/Home.tsx → protected route
 */
export function App() {
  const { initialize, isInitialized } = useAuthStore();

  useEffect(() => {
    console.debug('[App] initializing auth');
    initialize();
  }, [initialize]);

  if (!isInitialized) {
    return (
      <div className="auth-guard-loading">
        <div className="auth-guard-spinner" />
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text)',
            fontFamily: "'Outfit', sans-serif",
          },
        }}
      />
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <Home />
            </AuthGuard>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// --- Related Files ---
// CONNECTED TO:
//   - src/stores/authStore.ts — auth initialization
//   - src/routes/auth/Login.tsx — login route
//   - src/routes/auth/Signup.tsx — signup route
//   - src/routes/Home.tsx — protected home route
//   - src/components/AuthGuard.tsx — route protection
// USED BY:
//   - src/main.tsx — renders this component
